import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { notificationsAPI } from '../../lib/api';
import type {
  NotificationDigestFrequency,
  NotificationSubscriptionInput,
  NotificationSubscriptionSide,
  NotificationSubscriptionTree,
} from '../../lib/api';

/** The channels this screen lets a person control. */
export const SUBSCRIPTION_CHANNELS = ['bell', 'email'] as const;
export type SubscriptionChannel = (typeof SUBSCRIPTION_CHANNELS)[number];

/** One row of a `bulk_set` write. */
type SubscriptionRow = NotificationSubscriptionInput & {
  scope_type: NonNullable<NotificationSubscriptionInput['scope_type']>;
  scope_key: string;
};

interface UseSubscriptionTree {
  tree: NotificationSubscriptionTree | null;
  loading: boolean;
  saving: boolean;
  /** The side currently being edited; `''` for a user with no side. */
  side: NotificationSubscriptionSide | '';
  /** Switch to the other side. Re-fetches — the tree is resolved per side. */
  setSide: (side: NotificationSubscriptionSide) => void;
  /** Mute/unmute one channel at one scope. */
  setMuted: (
    scopeType: SubscriptionRow['scope_type'],
    scopeKey: string,
    channel: SubscriptionChannel,
    muted: boolean,
  ) => Promise<void>;
  /** Set the delivery cadence at one scope (both channels). */
  setCadence: (
    scopeType: SubscriptionRow['scope_type'],
    scopeKey: string,
    frequency: NotificationDigestFrequency,
  ) => Promise<void>;
  /** Drop this scope's own rows so the level above applies again. */
  resetScope: (
    scopeType: SubscriptionRow['scope_type'],
    scopeKey: string,
  ) => Promise<void>;
}

/**
 * Loads and writes the per-user notification subscription tree.
 *
 * Preferences are held per portal side, because a company that both buys and
 * sells has people on each. The tree is resolved server-side for one side at a
 * time, so switching sides re-fetches rather than filtering locally, and every
 * write carries the side it was made on.
 *
 * Every write goes through `bulk_set`, which is atomic, and is followed by a
 * re-fetch rather than a local patch: the value that applies at a node is
 * *resolved* from the levels above it, so muting a domain changes the
 * effective state of every event beneath it. Guessing that client-side would
 * drift from what the server will actually do at send time.
 *
 * @returns the tree, its load/save flags, and the three write helpers.
 */
export function useSubscriptionTree(): UseSubscriptionTree {
  const [tree, setTree] = useState<NotificationSubscriptionTree | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Empty until the first response says which side the server chose for us —
  // asking for a side before that would be guessing at the user's entitlement.
  const [side, setSideState] = useState<NotificationSubscriptionSide | ''>('');

  const load = useCallback(async (
    requested?: NotificationSubscriptionSide,
  ) => {
    try {
      const response = await notificationsAPI.getSubscriptionTree(requested);
      setTree(response.data);
      // The server is the authority on which side this is: it coerces a side
      // the user is not entitled to, so echoing the request back would leave
      // the tab highlighting a side the tree is not actually showing.
      setSideState(response.data.side);
    } catch (err: unknown) {
      console.error('Failed to load notification subscriptions:', err);
      toast.error('Could not load your notification settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setSide = useCallback((next: NotificationSubscriptionSide) => {
    setLoading(true);
    load(next);
  }, [load]);

  const write = useCallback(async (rows: SubscriptionRow[]) => {
    setSaving(true);
    try {
      await notificationsAPI.bulkSetSubscriptions(rows, side || undefined);
      await load(side || undefined);
    } catch (err: unknown) {
      console.error('Failed to save notification subscriptions:', err);
      toast.error('Could not save that change.');
    } finally {
      setSaving(false);
    }
  }, [load, side]);

  const setMuted: UseSubscriptionTree['setMuted'] = useCallback(
    (scopeType, scopeKey, channel, muted) =>
      write([{ scope_type: scopeType, scope_key: scopeKey, channel, muted }]),
    [write],
  );

  const setCadence: UseSubscriptionTree['setCadence'] = useCallback(
    (scopeType, scopeKey, frequency) =>
      // Cadence is a property of the scope, not of one channel, so it is
      // written against every channel this screen exposes.
      write(SUBSCRIPTION_CHANNELS.map(channel => ({
        scope_type: scopeType,
        scope_key: scopeKey,
        channel,
        digest_frequency: frequency,
      }))),
    [write],
  );

  const resetScope: UseSubscriptionTree['resetScope'] = useCallback(
    (scopeType, scopeKey) =>
      write(SUBSCRIPTION_CHANNELS.map(channel => ({
        scope_type: scopeType,
        scope_key: scopeKey,
        channel,
        reset: true,
      }))),
    [write],
  );

  return {
    tree, loading, saving, side, setSide, setMuted, setCadence, resetScope,
  };
}
